import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CmSettingsService } from '@ats/cm/core';
import { Unsubscribable } from '@ats/utility';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

const URLS_TO_IGNORE: string[] = ['/not-found', '/session-expired'];
const FALLBACK_URL: string[] = [];
const MAX_PAGE_HISTORY = 10;

@Injectable({ providedIn: 'root' })
export class CmPageHistoryService extends Unsubscribable {
	readonly hidePageBack$: Observable<boolean>;
	pageHistory: string[] = [];
	subscribed: boolean = false;

	private readonly hidePageBack = new BehaviorSubject<boolean>(false);

	constructor(private router: Router, private settings: CmSettingsService) {
		super();
		this.hidePageBack$ = this.hidePageBack.asObservable();
		this.initialize();
	}

	initialize() {
		if (!this.subscribed) {
			this.router.events
				.pipe(
					distinctUntilChanged(),
					filter(event => event instanceof NavigationEnd),
					takeUntil(this.unsub)
				)
				.subscribe((event: NavigationEnd) => {
					const url = URLS_TO_IGNORE.some(path => event.urlAfterRedirects.endsWith(path))
						? null
						: event.urlAfterRedirects;
					if (url) {
						this.addItemToHistory(event.urlAfterRedirects);
					}
				});
			}
		this.subscribed = true;
	}

	reportBadURL(url: string) {
		this.pageHistory = this.pageHistory.filter(path => path !== url);
	}

	back(): void {
		if (this.pageHistory.length === 0) {
			this.goToFallbackUrl();
		} else {
			this.router.navigateByUrl(this.pageHistory.pop());
		}
		return;
	}

	private goToFallbackUrl(): void {
		this.router.navigate(this.settings.route(FALLBACK_URL));
	}

	/**
	 *
	 * @param  {string} item The url string
	 * @returns {void}
	 */
	private addItemToHistory(item: string): void {
		if (this.pageHistory.length >= MAX_PAGE_HISTORY) {
			this.pageHistory.shift();
			this.pageHistory.push(item);
			return;
		}
		this.pageHistory.push(item);
		return;
	}
}
